import React, { useEffect, Suspense, lazy, useRef } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import Layout from './components/Layout';

// Critical public pages — loaded eagerly
import HomePage from './pages/HomePage';

// Після деплою файли чанків отримують нові імена, а у вкладки, відкритої до
// деплою, у пам'яті лишилися старі. Перший же перехід тоді падає з
// ChunkLoadError і людина бачить білий екран. Єдине лікування — перезавантажити
// сторінку: новий main.js знає нові імена.
const lazyRetry = (importer) =>
  lazy(() =>
    importer().catch((error) => {
      const message = String(error?.message || '');
      const isChunkError =
        /ChunkLoadError/i.test(message) ||
        /Loading chunk [\d]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message);
      if (isChunkError && typeof window !== 'undefined') {
        window.location.reload();
        return new Promise(() => {});   // не віддаємо керування до перезавантаження
      }
      throw error;
    })
  );

// All other pages — lazy loaded (code-split into separate chunks)
const CatalogPage = lazyRetry(() => import('./pages/CatalogPage'));
const ProductPage = lazyRetry(() => import('./pages/ProductPage'));
const BlogPage = lazyRetry(() => import('./pages/BlogPage'));
const ArticlePage = lazyRetry(() => import('./pages/ArticlePage'));
const AboutPage = lazyRetry(() => import('./pages/AboutPage'));
const PaymentDeliveryPage = lazyRetry(() => import('./pages/PaymentDeliveryPage'));
const ContactsPage = lazyRetry(() => import('./pages/ContactsPage'));
const CartPage = lazyRetry(() => import('./pages/CartPage'));
const CheckoutPage = lazyRetry(() => import('./pages/CheckoutPage'));
const PaymentResultPage = lazyRetry(() => import('./pages/PaymentResultPage'));
const WishlistPage = lazyRetry(() => import('./pages/WishlistPage'));
// Ліниво, як і решта: ілюстрація не має лежати в головному бандлі заради
// сторінки, яку більшість відвідувачів не побачить ніколи.
const NotFoundPage = lazyRetry(() => import('./pages/NotFoundPage'));
const PartnerPage = lazyRetry(() => import('./pages/PartnerPage'));
const VitaminTestPage = lazyRetry(() => import('./pages/VitaminTestPage'));
// Legal pages — lazy loaded named exports
const OfferPage = lazyRetry(() => import('./pages/LegalPages').then(m => ({ default: m.OfferPage })));
const PrivacyPage = lazyRetry(() => import('./pages/LegalPages').then(m => ({ default: m.PrivacyPage })));
const ReturnsPage = lazyRetry(() => import('./pages/LegalPages').then(m => ({ default: m.ReturnsPage })));

// Admin — all lazy (never loaded on public site)
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminProductForm = lazy(() => import('./pages/admin/AdminProductForm'));
const AdminArticles = lazy(() => import('./pages/admin/AdminArticles'));
const AdminArticleForm = lazy(() => import('./pages/admin/AdminArticleForm'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderCreate = lazy(() => import('./pages/admin/AdminOrderCreate'));
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));

const Spinner = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--msa-ink-soft)' }}>
    Завантаження…
  </div>
);

// Fires track.pageView on every route change — analytics loaded lazily after first interaction
function PageTracker() {
  const location = useLocation();
  const trackRef = useRef(null);

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    import('./lib/analytics').then(({ track }) => {
      trackRef.current = track;
      track.pageView(location.pathname + location.search, document.title);
    });
  }, [location]);
  return null;
}

function App() {
  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <PageTracker />
          <Suspense fallback={<Spinner />}>
          <Routes>
            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/:id" element={<AdminProductForm />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="articles/:id" element={<AdminArticleForm />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="orders/new" element={<AdminOrderCreate />} />
              <Route path="orders/:id" element={<AdminOrderDetail />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>

            {/* Public */}
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/catalog/:categorySlug" element={<CatalogPage />} />
              <Route path="/partners" element={<PartnerPage />} />
              <Route path="/vitamin-test" element={<VitaminTestPage />} />
              <Route path="/product/:id" element={<ProductPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:id" element={<ArticlePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/payment-delivery" element={<PaymentDeliveryPage />} />
              <Route path="/contacts" element={<ContactsPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/payment/success" element={<PaymentResultPage status="success" />} />
              <Route path="/payment/fail" element={<PaymentResultPage status="fail" />} />
              <Route path="/wishlist" element={<WishlistPage />} />
              <Route path="/legal/offer" element={<OfferPage />} />
              <Route path="/legal/privacy" element={<PrivacyPage />} />
              <Route path="/legal/returns" element={<ReturnsPage />} />
              {/* Катч-олл. Досі його не було взагалі: на невідомій адресі
                  людина бачила шапку, підвал і порожнечу між ними. */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </CartProvider>
    </div>
  );
}

export default App;
